// Content based on: https://github.com/springload/draftail/blob/master/examples/sources/LinkSource.js
import React from "react";
import { connect } from "react-redux";
import { serviceCaller, ServiceCallerProps } from "../../../composition";
import withStyles from "isomorphic-style-loader/withStyles";
import * as linkify from "linkifyjs";
import {
  EditorState,
  EntityInstance,
  Modifier,
  ContentState,
  SelectionState,
} from "draft-js";
import ModalFrame from "../../../../toolkit/modalFrame";
import { Row, Button, Input, FormGroup } from "reactstrap";
import classNames from "classnames";
import s from "./link.scss";
import isEqual from "lodash.isequal";
import isNil from "lodash.isnil";
import ReactTooltip from "react-tooltip";

interface Props extends ServiceCallerProps {
  editorState: EditorState;
  onComplete: (EditorState) => void;
  onClose: () => void;
  entityType: {
    type: string;
  };
  entity?: EntityInstance;
}

interface State {
  preview: string;
  url: string;
  loading: boolean;
}

function normalizeStoredUrl(raw: string): string {
  const trimmed = raw.replace(/\s/g, "");
  if (!trimmed) return "";
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return trimmed;
  if (trimmed.startsWith("//")) return `https:${trimmed}`;
  return `https://${trimmed}`;
}

function isAcceptableLinkUrl(raw: string): boolean {
  const trimmed = raw.replace(/\s/g, "");
  if (!trimmed) return false;
  const hits = linkify.find(trimmed);
  if (hits.some((h) => h.type === "url")) return true;
  return /^[\w.-]+\.[a-z]{2,}([\w\-./?#%&+=:~@]*)?$/i.test(trimmed);
}

function getSelectedPlainText(
  contentState: ContentState,
  selection: SelectionState
): string {
  const startKey = selection.getStartKey();
  const endKey = selection.getEndKey();
  const startOffset = selection.getStartOffset();
  const endOffset = selection.getEndOffset();

  const startBlock = contentState.getBlockForKey(startKey);
  const endBlock = contentState.getBlockForKey(endKey);

  if (!startBlock || !endBlock) return "";

  if (startKey === endKey) {
    return startBlock.getText().slice(startOffset, endOffset);
  }

  const blocks = contentState.getBlocksAsArray();
  const parts: string[] = [];
  let capturing = false;

  for (let i = 0; i < blocks.length; i++) {
    const block = blocks[i];
    const key = block.getKey();

    if (key === startKey) {
      capturing = true;
      parts.push(block.getText().slice(startOffset));
    } else if (key === endKey) {
      parts.push(block.getText().slice(0, endOffset));
      break;
    } else if (capturing) {
      parts.push(block.getText());
    }
  }

  return parts.join("\n");
}

class LinkSource extends React.PureComponent<Props, State> {
  private static deriveInitialFields(props: Props): Pick<State, "preview" | "url"> {
    const contentState = props.editorState.getCurrentContent();
    const selection = props.editorState.getSelection();
    const selectedText = selection.isCollapsed()
      ? ""
      : getSelectedPlainText(contentState, selection);

    const urlList: Array<{ type: string; href: string }> =
      linkify.find(selectedText);

    let url =
      isNil(urlList) ||
      urlList.length === 0 ||
      urlList.length > 1 ||
      urlList[0].type !== "url"
        ? ""
        : urlList[0].href;

    let preview = selectedText;

    if (!isNil(props.entity)) {
      const entityData = props.entity.getData();
      url = entityData.url;
      const savedPreview = entityData.preview;
      if (typeof savedPreview === "string" && savedPreview.length > 0) {
        preview = savedPreview;
      }
    }

    return { preview, url };
  }

  constructor(props: Props) {
    super(props);
    this.state = {
      ...LinkSource.deriveInitialFields(props),
      loading: false,
    };
  }

  componentDidUpdate(prevProps: Props) {
    if (!isEqual(prevProps.entity, this.props.entity)) {
      this.saveData();
    }
  }

  onChangePreviewText = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ preview: event.target.value });
  };

  onChangeUrl = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ url: event.target.value });
  };

  onSubmit = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    const { editorState, entityType, onComplete, onClose } = this.props;
    const { url, preview } = this.state;

    const normalizedUrl = normalizeStoredUrl(url);
    let contentState: ContentState = editorState.getCurrentContent();
    const selection: SelectionState = editorState.getSelection();

    const normalizedSelection = selection.merge({
      isBackward: false,
      hasFocus: true,
    });

    contentState = Modifier.replaceText(
      contentState,
      normalizedSelection,
      preview
    );

    const blockKey = normalizedSelection.getAnchorKey();
    const insertStart = normalizedSelection.getStartOffset();

    const linkSelection = SelectionState.createEmpty(blockKey).merge({
      anchorOffset: insertStart,
      focusOffset: insertStart + preview.length,
      hasFocus: true,
      isBackward: false,
    });

    contentState = contentState.createEntity(entityType.type, "MUTABLE", {
      url: normalizedUrl,
      preview,
    });
    const entityKey = contentState.getLastCreatedEntityKey();
    contentState = Modifier.applyEntity(contentState, linkSelection, entityKey);

    const newEditorState = EditorState.push(
      editorState,
      contentState,
      "apply-entity"
    );

    onComplete(newEditorState);
    onClose();
  };

  private saveData = () => {
    this.setState(LinkSource.deriveInitialFields(this.props));
  };

  render() {
    const { preview, url } = this.state;
    const { onClose } = this.props;

    const disableSave =
      preview.length === 0 || !isAcceptableLinkUrl(url);

    return (
      <ModalFrame title="Insert URL" size="md" isOpen={true} toggle={onClose}>
        <ReactTooltip
          id="tooltip-link-url"
          type="info"
          place="right"
          effect="float"
          delayHide={500}
        >
          Enter a full URL or a domain (for example{" "}
          <strong style={{ textDecoration: "underline" }}>www.example.com</strong>
          ). If you omit <strong style={{ textDecoration: "underline" }}>http://</strong>{" "}
          or <strong style={{ textDecoration: "underline" }}>https://</strong>,{" "}
          <strong style={{ textDecoration: "underline" }}>https://</strong> is added when
          you save.
        </ReactTooltip>
        <FormGroup>
          <label className={s.label} htmlFor="preview-content">
            Preview Text
          </label>
          <Input
            className={s.modalTextArea}
            type="text"
            name="preview-content"
            id="preview-content"
            value={preview}
            placeholder="Example"
            onChange={this.onChangePreviewText}
          />
          <label className={s.label} htmlFor="url-content">
            Link URL
          </label>
          &nbsp;
          <span data-tip="tooltip-link-url" data-for="tooltip-link-url">
            <i className="fa fa-question-circle" />
          </span>
          <Input
            className={s.modalTextArea}
            type="text"
            name="url-content"
            id="url-content"
            value={url}
            placeholder="www.example.com or https://www.example.com"
            onChange={this.onChangeUrl}
          />
        </FormGroup>

        <Row className={s.modalButton}>
          <Button
            onClick={this.onSubmit}
            disabled={disableSave}
            className={classNames(s.button, s.centered)}
          >
            {this.state.loading ? (
              <i className="fa fa-spinner fa-spin">&nbsp;</i>
            ) : (
              "SAVE"
            )}
          </Button>
        </Row>
      </ModalFrame>
    );
  }
}

export default serviceCaller(
  connect(
    () => ({}),
    () => ({})
  )(withStyles(s)(LinkSource))
);
